import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getProjectOffers } from "@/app/actions/offer/get/action";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { UCookie } from "@/app/utils/UCookie";
import { ULogger } from "@/app/utils/ULogger";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";

function saveCurrentOfferId(id?: number): void {
  if (id === undefined) {
    UCookie.remove(CookieKey.SELECTED_OFFER);
    return;
  }
  UCookie.write(CookieKey.SELECTED_OFFER, String(id), WORKSPACE_COOKIE_MAX_AGE);
}

function resolveCurrentId(offers: ProjectOfferData[], preferredId?: number): number | undefined {
  if (preferredId && offers.some((offer) => offer.id === preferredId)) {
    return preferredId;
  }

  const saved = UCookie.readNumber(CookieKey.SELECTED_OFFER);
  if (saved && offers.some((offer) => offer.id === saved)) {
    return saved;
  }

  return offers[0]?.id;
}

export type OfferState = {
  offers: ProjectOfferData[];
  currentOfferId?: number;
  offersProjectId?: number;
  setOffers(projectId: number, offers: ProjectOfferData[]): void;
  setCurrentOfferId(offerId: number): void;
  addOffer(offer: ProjectOfferData): void;
  updateOffer(offer: ProjectOfferData): void;
  fetchOffers(projectId: number): Promise<void>;
  refreshOffers(projectId: number): Promise<void>;
};

export const OfferStore = create<OfferState>()(
  devtools((set, getState) => ({
    offers: [],
    currentOfferId: undefined,
    offersProjectId: undefined,

    setOffers(projectId, offers) {
      const preferred =
        getState().offersProjectId === projectId ? getState().currentOfferId : undefined;
      const currentOfferId = resolveCurrentId(offers, preferred);

      if (currentOfferId !== getState().currentOfferId) {
        saveCurrentOfferId(currentOfferId);
      }

      set({ offers, currentOfferId, offersProjectId: projectId });
    },

    setCurrentOfferId(offerId) {
      if (offerId === getState().currentOfferId) {
        return;
      }

      saveCurrentOfferId(offerId);
      set({ currentOfferId: offerId });
    },

    addOffer(offer) {
      saveCurrentOfferId(offer.id);
      set({ offers: [...getState().offers, offer], currentOfferId: offer.id });
    },

    updateOffer(offer) {
      set({ offers: getState().offers.map((o) => (o.id === offer.id ? offer : o)) });
    },

    async fetchOffers(projectId) {
      if (getState().offersProjectId === projectId) {
        return;
      }

      await getState().refreshOffers(projectId);
    },

    async refreshOffers(projectId) {
      try {
        getState().setOffers(projectId, await getProjectOffers(projectId));
      } catch (error) {
        ULogger.error("Failed to fetch project offers", error);
      }
    },
  })),
);
